/**
 * CartClientsPage - Carte Leaflet des clients géolocalisés
 * - Markers colorés par chaleur (chaud/tiede/froid)
 * - Popup avec nom + bouton Fiche 360°
 * - Liste des clients sans coordonnées GPS (pour enrichir via Pappers)
 */
import { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Search, Building2, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Custom markers par chaleur
const makeIcon = (color) => L.divIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color:${color};width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);color:white;font-size:14px;">🏢</span></div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
  popupAnchor: [0, -26],
});

const iconChaud = makeIcon('#10b981');
const iconTiede = makeIcon('#f59e0b');
const iconFroid = makeIcon('#6b7280');
const iconDefault = makeIcon('#3b82f6');

export default function CarteClientsPage() {
  const [data, setData] = useState({ clients_localisable: [], clients_sans_coords: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [enrichiStats, setEnrichiStats] = useState({});
  const [filter, setFilter] = useState('');
  const [enriching, setEnriching] = useState(null);
  const [siretInput, setSiretInput] = useState({});
  const navigate = useNavigate();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [geoRes, enrichiRes] = await Promise.all([
        api.get('/crm-map/clients-geo'),
        api.get('/crm-enrichi/clients-enrichis').catch(() => ({ data: { clients: [] } })),
      ]);
      setData(geoRes.data);
      // Build lookup by id → chaleur
      const byId = {};
      for (const c of enrichiRes.data.clients || []) {
        byId[c.id] = c.stats || {};
      }
      setEnrichiStats(byId);
    } catch (e) {
      toast.error("Impossible de charger la carte");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const center = useMemo(() => {
    const locs = data.clients_localisable || [];
    if (locs.length === 0) return [46.6, 2.5]; // France center
    const avgLat = locs.reduce((s, c) => s + (c.latitude || 0), 0) / locs.length;
    const avgLon = locs.reduce((s, c) => s + (c.longitude || 0), 0) / locs.length;
    return [avgLat, avgLon];
  }, [data.clients_localisable]);

  const filtered = useMemo(() => {
    const f = filter.toLowerCase().trim();
    if (!f) return data.clients_sans_coords || [];
    return (data.clients_sans_coords || []).filter(c =>
      (c.nom || '').toLowerCase().includes(f) ||
      (c.ville || '').toLowerCase().includes(f)
    );
  }, [data.clients_sans_coords, filter]);

  const enrichir = async (client) => {
    const siren = (siretInput[client.id] || client.siren || '').replace(/\D/g, '');
    if (!siren || (siren.length !== 9 && siren.length !== 14)) {
      toast.error("SIREN (9 chiffres) ou SIRET (14) requis");
      return;
    }
    setEnriching(client.id);
    try {
      await api.post(`/pappers/enrichir-client/${client.id}?siren=${siren}`);
      toast.success(`${client.nom} enrichi · géolocalisé`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec enrichissement Pappers");
    } finally { setEnriching(null); }
  };

  const pickIcon = (c) => {
    const chaleur = (enrichiStats[c.id] || {}).chaleur;
    if (chaleur === 'chaud') return iconChaud;
    if (chaleur === 'tiede') return iconTiede;
    if (chaleur === 'froid') return iconFroid;
    return iconDefault;
  };

  return (
    <div className="p-6 space-y-4" data-testid="carte-clients-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MapPin className="w-6 h-6 text-emerald-600" />
          Carte des Clients
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {data.total_localisable} géolocalisés · {data.total_non_localise} à enrichir · {data.total} total
          </span>
          <div className="flex gap-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500" /> Chaud</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500" /> Tiède</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gray-500" /> Froid</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Carte */}
          <Card className="lg:col-span-2 overflow-hidden" data-testid="map-container-card">
            <div style={{ height: '70vh', width: '100%' }}>
              <MapContainer center={center} zoom={7} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                />
                {(data.clients_localisable || []).map((c) => (
                  <Marker key={c.id} position={[c.latitude, c.longitude]} icon={pickIcon(c)}>
                    <Popup>
                      <div className="min-w-[220px]">
                        <h3 className="font-bold text-base">{c.nom}</h3>
                        {c.activite && <p className="text-xs text-gray-500">{c.activite}</p>}
                        {c.adresse && <p className="text-xs mt-1">{c.adresse}</p>}
                        {c.ville && <p className="text-xs">{c.code_postal} {c.ville}</p>}
                        <Button
                          size="sm"
                          className="mt-2 w-full"
                          onClick={() => navigate(`/clients/${c.id}`)}
                          data-testid={`btn-popup-fiche-${c.id}`}
                        >
                          🎯 Fiche 360°
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </Card>

          {/* Liste clients sans géoloc */}
          <Card>
            <CardContent className="p-4">
              <h2 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                À enrichir Pappers ({data.total_non_localise})
              </h2>
              <div className="relative mb-2">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Rechercher..." value={filter} onChange={(e) => setFilter(e.target.value)} className="pl-8" data-testid="input-search-non-geoloc" />
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {filtered.length === 0 && (
                  <p className="text-xs text-center text-muted-foreground py-8">Aucun client sans coordonnées</p>
                )}
                {filtered.slice(0, 50).map((c) => (
                  <div key={c.id} className="p-2 border rounded text-xs space-y-1.5" data-testid={`client-non-geoloc-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{c.nom || '(sans nom)'}</p>
                        <p className="text-muted-foreground truncate">{c.ville || '—'}</p>
                      </div>
                      <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => navigate(`/clients/${c.id}`)} title="Fiche 360">
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex gap-1">
                      <Input
                        placeholder="SIREN/SIRET"
                        value={siretInput[c.id] || c.siren || ''}
                        onChange={(e) => setSiretInput({ ...siretInput, [c.id]: e.target.value })}
                        className="h-7 text-xs"
                        data-testid={`input-siren-${c.id}`}
                      />
                      <Button
                        size="sm"
                        className="h-7 text-[10px] shrink-0"
                        onClick={() => enrichir(c)}
                        disabled={enriching === c.id}
                        data-testid={`btn-enrichir-${c.id}`}
                      >
                        {enriching === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Building2 className="w-3 h-3 mr-1" />Pappers</>}
                      </Button>
                    </div>
                  </div>
                ))}
                {filtered.length > 50 && (
                  <p className="text-xs text-center text-muted-foreground italic">+{filtered.length - 50} autres · utilisez la recherche</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
